import { ChevronRight } from 'lucide-preact'
import type {
  EmailBlastGateDecisionDto,
  EmailBlastVariantResultDto,
  EmailBlastViewDto,
} from '@/types/emailBlast'
import { BlastStateBadge } from './BlastStateBadge'
import { formatDateTime } from './engagementFormat'
import { lowerSafe } from '@/utils/strings'

/**
 * The forensic half of the blast page, behind one disclosure (ORB-706): gate checks, the state
 * timeline, the ramp plan and per-variant A/B results.
 *
 * "Rollout log", not "Engine detail" — engine is our word, and the page's own vocabulary everywhere
 * else is broadcast / send / rollout.
 *
 * Two rules this must keep. It opens by DEFAULT when the blast is paused, because that is exactly the
 * state where an operator needs the history. And the gate decision that CAUSED the current pause is
 * lifted into the spine's verdict rather than living only here — nothing that changes the answer to
 * "do I need to intervene" may sit behind a fold. The table keeps the full history including that row.
 */
export function RolloutLogDisclosure({ blast }: { blast: EmailBlastViewDto }) {
  const gateCount = blast.GateDecisions.length
  const stateCount = blast.StateHistory.length
  const summary = [
    `${gateCount} gate ${gateCount === 1 ? 'check' : 'checks'}`,
    `${stateCount} state ${stateCount === 1 ? 'change' : 'changes'}`,
  ].join(' · ')

  return (
    <details
      open={blast.State === 'Paused'}
      className="group rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800"
    >
      <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-semibold text-slate-700 marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-slate-200 [&::-webkit-details-marker]:hidden">
        <ChevronRight className="w-3.5 h-3.5 shrink-0 text-slate-400 transition-transform group-open:rotate-90 motion-reduce:transition-none" />
        Rollout log
        <span className="ml-auto text-xs font-normal text-slate-400 dark:text-slate-500">{summary}</span>
      </summary>

      <div className="space-y-6 border-t border-slate-200 px-4 py-4 dark:border-slate-700">
        <GateDecisionsBlock blast={blast} />
        <RampPlanBlock blast={blast} />
        <SplitTestBlock blast={blast} />
        <StateHistoryBlock blast={blast} />
      </div>
    </details>
  )
}

function BlockHeading({ children }: { children: string }) {
  return (
    <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{children}</h4>
  )
}

function GateDecisionsBlock({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <div className="space-y-2">
      <BlockHeading>Gate checks</BlockHeading>
      {blast.GateDecisions.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No gate checks have run yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">When</th>
                <th className="px-3 py-2 text-left font-medium">Decision</th>
                <th className="px-3 py-2 text-right font-medium">Bounce %</th>
                <th className="px-3 py-2 text-right font-medium">Complaint %</th>
                <th className="px-3 py-2 text-right font-medium">Sample</th>
                <th className="px-3 py-2 text-left font-medium">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {blast.GateDecisions.map((gate, index) => (
                <GateDecisionRow key={`${gate.Timestamp}-${index}`} gate={gate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function GateDecisionRow({ gate }: { gate: EmailBlastGateDecisionDto }) {
  // TripMetric is the honest test (ORB-706); the Decision-string match is the fallback for rows written
  // before the field reached this DTO.
  const paused = !!gate.TripMetric || lowerSafe(gate.Decision).includes('pause')
  return (
    <tr className={paused ? 'bg-amber-50/60 dark:bg-amber-900/10' : undefined}>
      <td className="whitespace-nowrap px-3 py-2 text-slate-500 dark:text-slate-400">{formatDateTime(gate.Timestamp)}</td>
      <td className="px-3 py-2">
        <span
          className={
            'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ' +
            (paused
              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200')
          }
        >
          {gate.Decision}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{gate.HardBouncePercent.toFixed(2)}%</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{gate.ComplaintPercent.toFixed(2)}%</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{gate.SampleSize.toLocaleString()}</td>
      <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{gate.Reason || '—'}</td>
    </tr>
  )
}

/**
 * The staged plan, only when there IS one. Every existing blast is single-stage, so this is absent from
 * nearly every page — which is right: it used to draw a row of phantom stages from an array the old
 * model default grew on every write. The plan arrives normalized from the API now, so length is a
 * truthful test. The pacing knobs it used to carry in a footnote moved into the spine.
 */
function RampPlanBlock({ blast }: { blast: EmailBlastViewDto }) {
  if (blast.RampStagePercents.length <= 1) return null
  return (
    <div className="space-y-2">
      <BlockHeading>Ramp plan</BlockHeading>
      <div className="flex flex-wrap items-center gap-2">
        {blast.RampStagePercents.map((percent, index) => {
          const done = index < blast.CurrentRampStage
          const active = index === blast.CurrentRampStage
          const tone = done
            ? 'bg-primary-600 text-white border-primary-600'
            : active
            ? 'bg-primary-100 text-primary-800 border-primary-400 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-600'
            : 'bg-slate-100 text-slate-500 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
          return (
            <span
              key={`${percent}-${index}`}
              className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-medium ${tone}`}
              aria-current={active ? 'step' : undefined}
            >
              {percent}%
            </span>
          )
        })}
        <span className="text-xs text-slate-500 dark:text-slate-400">
          Stage {Math.min(blast.CurrentRampStage + 1, blast.RampStagePercents.length)} of{' '}
          {blast.RampStagePercents.length}
        </span>
      </div>
    </div>
  )
}

function SplitTestBlock({ blast }: { blast: EmailBlastViewDto }) {
  if (!blast.IsSplitTest) return null
  const metricLabel = lowerSafe(blast.WinMetric ?? 'opens')
  return (
    <div className="space-y-2">
      <BlockHeading>Subject A/B test</BlockHeading>
      {blast.SplitTestResults.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Measuring {metricLabel} across {blast.SubjectVariantKeys.length} subjects; the winner is picked when the
          window closes.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Variant</th>
                <th className="px-3 py-2 text-right font-medium">Delivered</th>
                <th className="px-3 py-2 text-right font-medium">Open rate</th>
                <th className="px-3 py-2 text-right font-medium">Click rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {blast.SplitTestResults.map(result => (
                <SplitTestRow
                  key={result.VariantKey}
                  result={result}
                  isWinner={result.VariantKey === blast.WinningVariantKey}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function SplitTestRow({ result, isWinner }: { result: EmailBlastVariantResultDto; isWinner: boolean }) {
  const rate = (value: number) => (result.Delivered > 0 ? `${((value / result.Delivered) * 100).toFixed(1)}%` : '—')
  return (
    <tr className={isWinner ? 'bg-green-50/60 dark:bg-green-900/10' : undefined}>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded bg-slate-100 px-1.5 text-[11px] font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
            {result.VariantKey}
          </span>
          {isWinner && (
            <span className="text-[10px] font-bold uppercase tracking-wide text-green-700 dark:text-green-400">Winner</span>
          )}
        </span>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{result.Delivered.toLocaleString()}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{rate(result.Opened)}</td>
      <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{rate(result.Clicked)}</td>
    </tr>
  )
}

function StateHistoryBlock({ blast }: { blast: EmailBlastViewDto }) {
  return (
    <div className="space-y-2">
      <BlockHeading>State history</BlockHeading>
      {blast.StateHistory.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No transitions recorded yet.</p>
      ) : (
        <ol className="space-y-3">
          {blast.StateHistory.map((entry, index) => (
            <li key={`${entry.State}-${entry.Timestamp}-${index}`} className="flex items-start gap-3">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary-500" aria-hidden="true" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <BlastStateBadge state={entry.State} />
                  <span className="text-xs text-slate-400">{formatDateTime(entry.Timestamp)}</span>
                </div>
                {entry.Details && <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{entry.Details}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
